import { useState } from 'react';
import { useRouter } from 'next/router';
import Dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useUser } from '@auth0/nextjs-auth0/client';
import { Box } from '@mui/material';

import SkeletonWidget from 'components/SkeletonWidget';

import withFilterUtils from 'hocs/withFilterUtils';

import {
  isSalesExecutiveSession,
  getSalesExecutiveCode,
} from 'utils/auth/authHelper';

import { loadState } from 'utils/localStorage';

export const PersonalDataFormComponent = Dynamic(
  () => import('components/LeadFormComponent/PersonalDataFormComponent'),
  { ssr: true, loading: () => <SkeletonWidget /> }
);

export const PremiumPlanRedeem = Dynamic(
  () => import('components/LeadFormComponent/PremiumPlanRedeem'),
  { ssr: true, loading: () => <SkeletonWidget /> }
);

export const PlanningStepFormComponent = Dynamic(
  () => import('components/LeadFormComponent/PlanningStepFormComponent'),
  { ssr: false }
);

export const MeetingFormComponent = Dynamic(
  () => import('components/LeadFormComponent/MeetingFormComponent'),
  { ssr: false }
);

export const MessageUnlockComponent = Dynamic(
  () => import('components/MessageUnlockAbroadComponent'),
  { ssr: false }
);

function LeadFormComponent({
  age,
  buttonText,
  cityId,
  countries,
  countryId,
  courseId,
  id,
  lead,
  onMessageSuccess,
  onSubmitFirstStep,
  onSubmitSecondStep,
  onSubmitThirdStep,
  schoolsFilters,
  shouldScheduleMeeting,
}) {
  const router = useRouter();
  const { user } = useUser();
  const salesExecutiveCode =
    user && isSalesExecutiveSession(user) ? getSalesExecutiveCode(user) : null;
  const state = loadState();
  const { leadStore } = useSelector((store) => store.studentReducer);

  const countriesData = countries.filter((c) =>
    schoolsFilters.languageFilters.find((lf) => lf.slugName === c.slugName)
  );

  const selectedLead = lead || leadStore;

  const getInitialStep = () => {
    if (shouldScheduleMeeting) return 3;

    if (lead?.step) return lead?.step;

    return 1;
  };

  const getInitialMeet = () => {
    if (selectedLead?.country_travel) {
      return countriesData.filter(
        (c) => c.name === selectedLead.country_travel
      )[0]?.link;
    }

    return '';
  };

  const [meet, setMeet] = useState(getInitialMeet());
  const [step, setStep] = useState(getInitialStep());
  const [isPremiumPlanUser, setIsPremiumPlanUser] = useState(false);

  const handleRedeemClose = () => {
    setStep(2);

    setIsPremiumPlanUser(false);
  };

  const getStepContent = () => {
    if (isPremiumPlanUser) {
      return <PremiumPlanRedeem handleRedeemClose={handleRedeemClose} />;
    }

    switch (step) {
      case 1:
        return (
          <PersonalDataFormComponent
            age={age}
            buttonText={buttonText}
            cityId={cityId}
            countries={countriesData}
            countryId={countryId}
            courseId={courseId}
            id={`${id}-1`}
            internalStore={schoolsFilters}
            onSubmitFirstStep={onSubmitFirstStep}
            query={router?.query}
            salesExecutiveCode={salesExecutiveCode}
            setIsPremiumPlanUser={setIsPremiumPlanUser}
            setMeet={setMeet}
            setStep={setStep}
          />
        );

      case 2:
        return (
          <PlanningStepFormComponent
            buttonText={buttonText}
            id={`${id}-2`}
            loadedLead={selectedLead}
            onSubmitSecondStep={onSubmitSecondStep}
            query={router?.query}
            salesExecutiveCode={salesExecutiveCode}
            setStep={setStep}
          />
        );

      case 3: {
        const firstName =
          selectedLead?.firstname ||
          selectedLead?.firstName ||
          state.lead.firstname ||
          '';
        const email = selectedLead?.email || state.lead.email || '';
        const phone = selectedLead?.phone || state.lead.phone || '';

        return (
          <MeetingFormComponent
            firstName={firstName}
            buttonText={buttonText}
            meet={`${meet}&firstname=${firstName}&email=${email}&phone=${phone}`}
            onSubmitThirdStep={onSubmitThirdStep}
            setStep={setStep}
          />
        );
      }

      default:
        return (
          <MessageUnlockComponent
            countries={countriesData}
            onMessageSuccess={onMessageSuccess}
          />
        );
    }
  };

  return <Box sx={{ py: 2, px: { xs: '0', md: 3 } }}>{getStepContent()}</Box>;
}

LeadFormComponent.propTypes = {
  age: PropTypes.number,
  buttonText: PropTypes.string,
  cityId: PropTypes.number,
  countryId: PropTypes.number,
  courseId: PropTypes.number,
  id: PropTypes.string,
  lead: PropTypes.instanceOf(Object),
  onMessageSuccess: PropTypes.instanceOf(Function),
  onSubmitFirstStep: PropTypes.instanceOf(Function),
  onSubmitSecondStep: PropTypes.instanceOf(Function),
  onSubmitThirdStep: PropTypes.instanceOf(Function),
};

LeadFormComponent.defaultProps = {
  age: 0,
  buttonText: 'Avançar',
  cityId: 0,
  countryId: 0,
  courseId: 1,
  id: 'widget-sales-executive',
  lead: null,
  onMessageSuccess: () => {},
  onSubmitFirstStep: () => {},
  onSubmitSecondStep: () => {},
  onSubmitThirdStep: () => {},
};

export default withFilterUtils(LeadFormComponent);
